import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/ui-helpers";
import { StagesEditor, FieldsEditor } from "@/components/crm/SetupEditors";
import WorkflowHooks from "@/components/crm/WorkflowHooks";
import { useSearchParams } from "react-router-dom";
import SopsPanel from "@/components/crm/SopsPanel";
import ModulesEditor from "@/components/crm/ModulesEditor";
import ImportTool from "@/components/crm/ImportTool";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const TABS = [["sops", "SOPs"], ["stages", "Stages"], ["fields", "Fields"], ["modules", "Modules"], ["import", "Import"], ["webhooks", "Webhooks"]];
const OWN_SAVE = ["sops", "modules", "import", "webhooks"];

export default function CrmSetup() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get("tab") || "sops");
  const [cfg, setCfg] = useState(null);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    api.get("/crm/config").then((r) => setCfg(r.data));
  }, []);
  const edit = (p) => { setCfg((c) => ({ ...c, ...p })); setDirty(true); };
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const save = async () => {
    try {
      {
        const fields = cfg.fields.filter((f) => f.key && f.label).map(({ _new, ...f }) => f);
        const keys = fields.map((f) => f.key);
        if (new Set(keys).size !== keys.length) { toast.error("Field keys must be unique"); return; }
        const { data } = await api.put("/crm/config", {
          stages: cfg.stages.map((s) => s.trim()).filter(Boolean), fields, cadence: cfg.cadence, stage_colors: cfg.stage_colors || {},
        });
        setCfg(data);
      }
      setDirty(false); toast.success("Saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  if (!cfg) return <div className="p-6 text-xs text-neutral-500">Loading…</div>;
  return (
    <div data-testid="crm-setup-page">
      <PageHeader title="CRM setup" sub={isAdmin ? "SOPs · stages · fields · modules · import" : "View only: admins can edit"}
        right={isAdmin && !OWN_SAVE.includes(tab) && <button className="btn btn-primary" onClick={save} disabled={!dirty} data-testid="save-crm-setup">{dirty ? "Save" : "Saved"}</button>} />
      <div className="flex gap-1 px-4 md:px-6 border-b text-[13px] overflow-x-auto">
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`setup-tab-${k}`}
            className={`px-3 py-2 whitespace-nowrap border-b-2 -mb-px transition-colors ${tab === k ? "border-white text-white" : "border-transparent text-neutral-500 hover:text-neutral-300"}`}>{l}</button>
        ))}
      </div>
      {tab === "sops" && <div className="p-4 md:p-6"><SopsPanel isAdmin={isAdmin} /></div>}
      {isAdmin && tab === "modules" && <div className="p-4 md:p-6"><ModulesEditor /></div>}
      {isAdmin && tab === "import" && <div className="p-4 md:p-6"><ImportTool /></div>}
      {tab === "webhooks" && <div className="p-4 md:p-6"><WorkflowHooks isAdmin={isAdmin} /></div>}
      {!isAdmin && ["modules", "import"].includes(tab) && <div className="p-6 text-xs text-neutral-500">Admins only.</div>}
      <div className={`p-4 md:p-6 ${isAdmin ? "" : "pointer-events-none opacity-70"} ${OWN_SAVE.includes(tab) ? "hidden" : ""}`}>
        {tab === "stages" && <StagesEditor stages={cfg.stages} colors={cfg.stage_colors || {}} onColors={(stage_colors) => edit({ stage_colors })} onChange={(stages) => edit({ stages })} />}
        {tab === "fields" && <FieldsEditor fields={cfg.fields} onChange={(fields) => edit({ fields })} />}
      </div>
    </div>
  );
}
